import moment from "moment";
import type { CalendarEvent, GenerateICSOptions } from "../calendar.types";

/**
 * Generates ICS (iCalendar) file content for an array of events
 *
 * @param events - Array of events with start_time and end_time
 * @param options - Options for event title, description, and URL
 * @returns ICS file content as a string
 *
 * @example
 * ```typescript
 * const events = [
 *   { id: 1, start_time: "2025-03-15T14:00:00Z", end_time: "2025-03-15T15:00:00Z" },
 *   { id: 2, start_time: "2025-03-22T14:00:00Z", end_time: "2025-03-22T15:00:00Z" },
 * ];
 *
 * const icsContent = generateICSContent(events, {
 *   eventTitle: "Team Meeting",
 *   eventDescription: "Weekly sync",
 *   eventUrl: "https://example.com/meeting",
 * });
 *
 * // Create blob and download
 * const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
 * ```
 */
export function generateICSContent(
  events: CalendarEvent[],
  options: GenerateICSOptions
): string {
  const { eventTitle, eventDescription, eventUrl } = options;

  const icsContent = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Pollinator//Pollinator Platform//EN",
    "CALSCALE:GREGORIAN",
  ];

  events.forEach((event) => {
    const startTime =
      moment.utc(event.start_time).format("YYYYMMDDTHHmmss") + "Z";
    const endTime = moment.utc(event.end_time).format("YYYYMMDDTHHmmss") + "Z";
    const now = moment.utc().format("YYYYMMDDTHHmmss") + "Z";

    icsContent.push(
      "BEGIN:VEVENT",
      `DTSTART:${startTime}`,
      `DTEND:${endTime}`,
      `DTSTAMP:${now}`,
      `UID:event-${event.id}-${event.start_time}@pollinator.coop`,
      `SUMMARY:${eventTitle}`,
      `DESCRIPTION:${eventDescription || ""}`,
      `URL:${eventUrl || ""}`,
      "STATUS:CONFIRMED",
      "END:VEVENT"
    );
  });

  icsContent.push("END:VCALENDAR");

  return icsContent.join("\r\n");
}

export default generateICSContent;
